import React, { useEffect, useMemo, useRef } from "react";
import { FaImage, FaSave, FaTimes, FaUpload } from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/partenairesPhotoServices";

import {
  PARTENAIRE_FALLBACK_IMAGE,
  PARTENAIRE_PHOTO_TARGETS,
} from "../utils/partenairesSettingsUtils";

export default function PartenaireImageUploadModal({
  isOpen,
  partenaire,
  uploadTarget,
  selectedFile,
  filePreview,
  uploading,
  uploadProgress,
  onClose,
  onTargetChange,
  onFileChange,
  onSubmit,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    const timeout = setTimeout(() => {
      dialogRef.current?.querySelector("input[type='file']")?.focus();
    }, 0);

    return () => {
      window.removeEventListener("keydown", onEsc);
      clearTimeout(timeout);
    };
  }, [isOpen, onClose]);

  const targetConfig =
    PARTENAIRE_PHOTO_TARGETS[uploadTarget] ||
    PARTENAIRE_PHOTO_TARGETS.photo_url;

  const currentImage = useMemo(() => {
    const raw = partenaire?.[uploadTarget] || "";
    return toAbsolutePhotoUrl(raw) || PARTENAIRE_FALLBACK_IMAGE;
  }, [partenaire, uploadTarget]);

  if (!isOpen || !partenaire) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Changer l’image du partenaire"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Upload image
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaImage className="text-indigo-600 dark:text-indigo-300" />
              {targetConfig.label}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Partenaire :{" "}
              <span className="font-bold text-slate-900 dark:text-white">
                {partenaire.nom}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 p-5">
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Image à modifier
            </label>

            <select
              value={uploadTarget}
              onChange={(event) => onTargetChange(event.target.value)}
              disabled={uploading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              {Object.values(PARTENAIRE_PHOTO_TARGETS).map((target) => (
                <option key={target.key} value={target.key}>
                  {target.label}
                </option>
              ))}
            </select>

            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {targetConfig.description}
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/70">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Image actuelle
              </p>

              <div className="h-44 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                <img
                  src={currentImage}
                  alt="Image actuelle"
                  className="h-full w-full object-contain p-2"
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={(event) => {
                    event.currentTarget.src = PARTENAIRE_FALLBACK_IMAGE;
                  }}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/70">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Nouvelle image
              </p>

              <div className="flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                {filePreview ? (
                  <img
                    src={filePreview}
                    alt="Aperçu nouvelle image"
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <FaUpload className="text-3xl text-slate-300 dark:text-slate-600" />
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Sélectionner une image
            </label>

            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={onFileChange}
              disabled={uploading}
              className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
            />

            {selectedFile ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {selectedFile.name} — {Math.round(selectedFile.size / 1024)} Ko
              </p>
            ) : null}
          </div>

          {uploading ? (
            <div>
              <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                <span>Transfert en cours</span>
                <span>{uploadProgress}%</span>
              </div>

              <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="submit"
              disabled={uploading || !selectedFile}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <FaSave />
              {uploading ? "Transfert..." : "Sauvegarder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}